import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { Download, Magnify, Pencil, Plus, TrashCan } from '@opengov/react-capital-assets';

import { CaseStatusBadge } from '../components/vcm/CaseStatusBadge';
import { ResourceFormDialog, type ResourceFormValues } from '../components/vcm/ResourceFormDialog';
import { REFERRAL_STATUSES } from '../lib/vcm/constants';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetCases, mockGetClientById, mockGetResources } from '../mocks/vcm-mock-data';
import type { TcrCase, TcrResource } from '../types/vcm';

const OPEN_STATUSES = new Set(['Open']);

type StatusChipColor = 'success' | 'error' | 'warning' | 'default';

const STATUS_COLOR: Record<string, StatusChipColor> = {
  Active: 'success',
  Inactive: 'error',
  'Under Review': 'warning',
};

const STATUS_OPTIONS = ['All statuses', 'Active', 'Inactive', 'Under Review'] as const;
const GEO_OPTIONS = ['All geographic areas', 'Statewide', 'Regional', 'County-specific'] as const;

/** Escape a CSV cell — wrap in quotes (doubling inner quotes) when it contains a comma, quote, or newline. */
function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Build a CSV string from a header row + data rows, then trigger a client-side download. */
function downloadCsv(filename: string, headers: readonly string[], rows: readonly string[][]): void {
  const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type ReferStep = 'pick-case' | 'fill-details' | 'success';

interface ReferralDetails {
  serviceName: string;
  agencyName: string;
  status: string;
  units: string;
  amount: string;
  notes: string;
}

function ReferToCaseDialog({
  resource,
  openCases,
  onClose,
}: {
  resource: TcrResource;
  openCases: TcrCase[];
  onClose: () => void;
}) {
  const [step, setStep] = useState<ReferStep>('pick-case');
  const [caseQuery, setCaseQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState<TcrCase | null>(null);
  const [details, setDetails] = useState<ReferralDetails>({
    serviceName: resource.service,
    agencyName: resource.organization,
    status: 'Requested',
    units: '',
    amount: '',
    notes: '',
  });

  const filteredCases = useMemo(() => {
    const q = caseQuery.toLowerCase().trim();
    if (!q) return openCases;
    return openCases.filter((c) => {
      const client = mockGetClientById(c.clientId);
      const clientName = client ? `${client.firstName} ${client.lastName}` : '';
      return (
        clientName.toLowerCase().includes(q) ||
        c.caseId.toLowerCase().includes(q) ||
        c.county.toLowerCase().includes(q) ||
        c.assignedManager.toLowerCase().includes(q) ||
        ( c.program ?? '' ).toLowerCase().includes(q)
      );
    });
  }, [openCases, caseQuery]);

  const handleCaseSelect = (c: TcrCase) => {
    setSelectedCase(c);
    setStep('fill-details');
  };

  const handleSubmit = () => {
    // MOCK — replace with real API call when backend is connected
    setStep('success');
  };

  const titles: Record<ReferStep, string> = {
    'pick-case': `Refer to case — ${resource.name}`,
    'fill-details': 'Referral details',
    'success': 'Referral added',
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{titles[step]}</DialogTitle>
      <DialogContent dividers sx={{ height: 480, display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 2 }}>

        {/* Step 1: pick a case */}
        {step === 'pick-case' && (
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by client name, case ID, county, or case manager"
              value={caseQuery}
              onChange={(e) => setCaseQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <Box sx={{ display: 'flex', mr: 1, color: (t) => t.palette.text.secondary, '& svg': { width: 16, height: 16 } }}>
                      <Magnify />
                    </Box>
                  ),
                },
              }}
            />
            {filteredCases.length === 0 ? (
              <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary, py: 2, textAlign: 'center' }}>
                No cases match your search.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {filteredCases.map((c) => {
                  const client = mockGetClientById(c.clientId);
                  const clientName = client ? `${client.firstName} ${client.lastName}` : c.clientId;
                  return (
                    <Card key={c.caseId} variant="outlined" sx={{ flexShrink: 0 }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{clientName}</Typography>
                            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary, display: 'block' }}>
                              {c.caseId}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip label={c.county} size="small" variant="outlined" />
                              <Chip label={`Tier ${c.tier}`} size="small" variant="outlined" />
                              <Chip label={c.program} size="small" variant="outlined" color="info" />
                            </Stack>
                            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary, display: 'block', mt: 0.5 }}>
                              Case manager: {c.assignedManager}
                            </Typography>
                          </Box>
                          <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                            <CaseStatusBadge status={c.status} />
                            <Button variant="contained" size="small" onClick={() => handleCaseSelect(c)}>
                              Select
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}

        {/* Step 2: fill referral details */}
        {step === 'fill-details' && selectedCase && (
          <Stack spacing={2} sx={{ flex: 1, overflow: 'auto', minHeight: 0, width: '100%' }}>
            {/* Selected case summary */}
            <Card variant="outlined" sx={{ flexShrink: 0, bgcolor: (t) => t.palette.action.hover }}>
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                {(() => {
                  const client = mockGetClientById(selectedCase.clientId);
                  const clientName = client ? `${client.firstName} ${client.lastName}` : selectedCase.clientId;
                  return (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{clientName}</Typography>
                        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                          {selectedCase.caseId} · {selectedCase.county} · {selectedCase.program}
                        </Typography>
                      </Box>
                      <CaseStatusBadge status={selectedCase.status} />
                    </Stack>
                  );
                })()}
              </CardContent>
            </Card>

            <TextField
              label="Service"
              size="small"
              fullWidth
              value={details.serviceName}
              onChange={(e) => setDetails((p) => ({ ...p, serviceName: e.target.value }))}
            />
            <TextField
              label="Agency"
              size="small"
              fullWidth
              value={details.agencyName}
              onChange={(e) => setDetails((p) => ({ ...p, agencyName: e.target.value }))}
            />
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={details.status}
              onChange={(e) => setDetails((p) => ({ ...p, status: e.target.value }))}
            >
              {REFERRAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Units"
                size="small"
                type="number"
                fullWidth
                value={details.units}
                onChange={(e) => setDetails((p) => ({ ...p, units: e.target.value }))}
              />
              <TextField
                label="Amount ($)"
                size="small"
                type="number"
                fullWidth
                value={details.amount}
                onChange={(e) => setDetails((p) => ({ ...p, amount: e.target.value }))}
              />
            </Stack>
            <TextField
              label="Notes"
              size="small"
              fullWidth
              multiline
              rows={4}
              placeholder="Additional details about this referral..."
              value={details.notes}
              onChange={(e) => setDetails((p) => ({ ...p, notes: e.target.value }))}
            />
          </Stack>
        )}

        {/* Step 3: success */}
        {step === 'success' && selectedCase && (
          <Alert severity="success">
            Referral for <strong>{resource.name}</strong> added to{' '}
            <strong>
              {(() => {
                const client = mockGetClientById(selectedCase.clientId);
                return client ? `${client.firstName} ${client.lastName}` : selectedCase.clientId;
              })()}
            </strong>{' '}
            ({selectedCase.caseId}).
          </Alert>
        )}

      </DialogContent>
      <DialogActions>
        {step === 'pick-case' && (
          <Button onClick={onClose}>Cancel</Button>
        )}
        {step === 'fill-details' && (
          <>
            <Button onClick={() => setStep('pick-case')}>Back</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!details.serviceName.trim()}
            >
              Add referral
            </Button>
          </>
        )}
        {step === 'success' && (
          <Button onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function ResourceLibraryPage() {
  /* MOCK DATA — editable in-session (Add/Edit/Delete) until /v2 is wired. */
  const [resources, setResources] = useState<TcrResource[]>(() => mockGetResources());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TcrResource | null>(null);
  const [referResource, setReferResource] = useState<TcrResource | null>(null);

  const openCases = useMemo(
    () => mockGetCases().filter((c) => OPEN_STATUSES.has(c.status)),
    [],
  );

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (resource: TcrResource) => {
    setEditing(resource);
    setDialogOpen(true);
  };
  const handleDelete = (id: string) => setResources((prev) => prev.filter((r) => r.id !== id));
  const handleSave = (values: ResourceFormValues) => {
    const now = new Date().toISOString();
    setResources((prev) => {
      if (editing) {
        return prev.map((r) => (r.id === editing.id ? { ...r, ...values, updatedAt: now } : r));
      }
      return [{ id: `res-${Date.now()}`, ...values, createdAt: now, updatedAt: now }, ...prev];
    });
    setDialogOpen(false);
  };

  const [query, setQuery] = useState('');
  const [countyFilter, setCountyFilter] = useState('All counties');
  const [recoveryNeedFilter, setRecoveryNeedFilter] = useState('All recovery needs');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [geoFilter, setGeoFilter] = useState('All geographic areas');

  const counties = useMemo(
    () => ['All counties', ...new Set(resources.map((r) => r.county))],
    [resources],
  );
  const categories = useMemo(
    () => ['All recovery needs', ...new Set(resources.map((r) => r.category))],
    [resources],
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    let results: TcrResource[] = resources;
    if (q) {
      results = results.filter((r) =>
        `${r.name} ${r.category} ${r.service} ${r.organization}`.toLowerCase().includes(q),
      );
    }
    if (countyFilter !== 'All counties') {
      results = results.filter((r) => r.county === countyFilter);
    }
    if (recoveryNeedFilter !== 'All recovery needs') {
      results = results.filter((r) => r.category === recoveryNeedFilter);
    }
    if (statusFilter !== 'All statuses') {
      results = results.filter((r) => r.status === statusFilter);
    }
    if (geoFilter !== 'All geographic areas') {
      results = results.filter((r) => r.geographicArea === geoFilter);
    }
    return results;
  }, [resources, query, countyFilter, recoveryNeedFilter, statusFilter, geoFilter]);

  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `tcr-resource-library-${today}.csv`,
      ['Name', 'Category', 'County', 'Service', 'Organization', 'Status'],
      filtered.map((r) => [r.name, r.category, r.county, r.service, r.organization, r.status]),
    );
  };

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      {/* ── Page header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: '1.5px', color: (t) => t.palette.primary.main }}
          >
            Resource Management
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mt: (t) => t.spacing(0.5) }}>
            Resource Library
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: (t) => t.palette.text.secondary,
              mt: (t) => t.spacing(0.5),
              maxWidth: 720,
            }}
          >
            Browse the resource inventory available to veterans. Use filters to
            narrow results by county, recovery need, or status.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExport}>
            Export library
          </Button>
          <Button variant="contained" startIcon={<Plus />} onClick={openAdd}>
            Add resource
          </Button>
        </Stack>
      </Stack>

      {/* ── Search shell ── */}
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="County"
                value={countyFilter}
                onChange={(e) => setCountyFilter(e.target.value)}
              >
                {counties.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Recovery need"
                value={recoveryNeedFilter}
                onChange={(e) => setRecoveryNeedFilter(e.target.value)}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Geographic area"
                value={geoFilter}
                onChange={(e) => setGeoFilter(e.target.value)}
              >
                {GEO_OPTIONS.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ my: (t) => t.spacing(2) }} />

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              fullWidth
              size="small"
              placeholder="Search by resource name, service, category, or organization"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <Box
                      sx={{
                        display: 'flex',
                        mr: 1,
                        color: (t) => t.palette.text.secondary,
                        '& svg': { width: 18, height: 18 },
                      }}
                    >
                      <Magnify />
                    </Box>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<Magnify />}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Search
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Results ── */}
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: (t) => t.spacing(1.5) }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Resources
          </Typography>
          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
            {filtered.length} results
          </Typography>
        </Stack>

        {filtered.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: (t) => t.spacing(4) }}>
              <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                No resources match your search. Adjust the filters or try a different query.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((resource) => (
              <Grid key={resource.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent
                    sx={{
                      flex: '1 1 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: (t) => t.spacing(1.5),
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {resource.name}
                      </Typography>
                      <Chip
                        label={resource.status}
                        size="small"
                        variant="outlined"
                        color={STATUS_COLOR[resource.status] ?? 'default'}
                        sx={{ flexShrink: 0 }}
                      />
                    </Stack>

                    <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                      {resource.service}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={resource.category}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      <Chip label={resource.county} size="small" variant="outlined" />
                    </Stack>

                    <Box sx={{ flex: '1 1 auto' }} />
                    <Divider />
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<Pencil />}
                          onClick={() => openEdit(resource)}
                          sx={{ px: 0 }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          startIcon={<TrashCan />}
                          onClick={() => handleDelete(resource.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setReferResource(resource)}
                      >
                        Refer to case
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Refer to case dialog */}
      {referResource !== null && (
        <ReferToCaseDialog
          resource={referResource}
          openCases={openCases}
          onClose={() => setReferResource(null)}
        />
      )}

      <ResourceFormDialog
        open={dialogOpen}
        resource={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
}
